import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppState } from '../../Store';
import { ActionTypes } from '../../Store/Stores/User';
import { logo } from '../../Resources/Images';
import { useTranslation } from '../../Translation';
import { useAPI } from '../../API';
import LoadingCircleTransparent from '../../Components/LoadingIndicators/LoadingCircleTransparent';
import { bgColor, classNames, disabledBg, focusOutline, focusVisible, hoverBgColor} from '../../Resources/CSS/styling';

export default function SetupMFA() {
    const [form, setForm] = useState({
        totp_secret: '',
        totp_code: ''
    });
    const [qrCode, setQrCode] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetchingSetup, setFetchingSetup] = useState(true);
    const { pathAfterLogin } = useAppState();

    const { translate } = useTranslation();
    const api = useAPI();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchMFASetup = async () => {
            try {
                setFetchingSetup(true);
                const response = await api.getMFASetup();
                setForm({
                    totp_secret: response.data.totp_secret || '',
                    totp_code: ''
                });
                // Assuming the backend returns qr_code as a data URI
                if (response.data.qr_code_data_uri) {
                    setQrCode(response.data.qr_code_data_uri);
                }
            } catch (error) {
                setError(error.message || translate('mfa.setupError') || 'Failed to load MFA setup');
                console.error('MFA setup error:', error);
            } finally {
                setFetchingSetup(false);
            }
        };

        fetchMFASetup();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            await api.setMFA(form.totp_secret, form.totp_code);
            
            // Redirect after successful setup
            navigate(pathAfterLogin || '/dashboard');
        } catch (error) {
            if (error.status === 400) {
                setError(translate('mfa.invalidCode') || 'Invalid verification code. Please try again.');
            } else {
                setError(error.message || translate('mfa.setupError') || 'Failed to set up MFA');
            }
            console.error('MFA setup error:', error);
        } finally {
            setLoading(false);
        }
    }

    // Format TOTP secret for display (add spaces every 4 characters)
    const formatSecret = (secret) => {
        if (!secret) return '';
        return secret.match(/.{1,4}/g)?.join(' ') || secret;
    };

    if (fetchingSetup) {
        return (
            <div className="flex min-h-full flex-1 w-full items-center justify-center">
                <div className="w-12 h-12">
                    <LoadingCircleTransparent />
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-full flex-1 w-full">
            <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:px-20 xl:px-24">
                <div className="mx-auto w-full max-w-md border-gray-200 rounded-md p-8">
                    
                    <div className="flex flex-col items-center mt-8">
                        <img src={logo} alt="logo" className="w-36" />
                        <h2 className="text-2xl/9 font-bold tracking-tight text-gray-900 mt-4">{translate('mfa.title') || 'MFA'}</h2>
                    </div>

                    <div className="mt-10">
                        <div className="mb-6">
                            <p className="text-sm text-gray-600 mb-4">
                                {translate('mfa.setup_description') || 'To set up two-factor authentication, scan the QR code below with your authenticator app (such as Google Authenticator, Authy, or Microsoft Authenticator), then enter the verification code to complete setup.'}
                            </p>
                            
                            {/* QR Code Display */}
                            {qrCode && (
                                <div className="flex flex-col items-center mb-6">
                                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                                        <img 
                                            src={qrCode} 
                                            alt="MFA QR Code" 
                                            className="w-64 h-64"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* TOTP Secret Display */}
                            {form.totp_secret && (
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {translate('mfa.secret_label') || 'Manual Entry Secret:'}
                                    </label>
                                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                                        <code className="text-sm font-mono text-gray-800 break-all">
                                            {formatSecret(form.totp_secret)}
                                        </code>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        {translate('mfa.secret_hint') || 'If you cannot scan the QR code, enter this secret manually in your authenticator app.'}
                                    </p>
                                </div>
                            )}
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {error && (
                                <div className="rounded-md bg-red-50 p-4">
                                    <div className="flex">
                                        <div className="text-sm text-red-700">
                                            {error}
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <div>
                                <label htmlFor="totp_code" className="block text-sm/6 font-medium text-gray-900">
                                    {translate('mfa.code_hint') || 'Enter the 6-digit code from your authenticator app.'}
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="totp_code"
                                        name="totp_code"
                                        type="text"
                                        required
                                        autoComplete="one-time-code"
                                        maxLength={6}
                                        pattern="[0-9]{6}"
                                        placeholder="000000"
                                        value={form.totp_code}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                            setForm({ ...form, totp_code: value });
                                        }}
                                        className={classNames(`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6 text-center text-2xl tracking-widest ${focusOutline}`)}
                                    />
                                </div>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={loading || !form.totp_code || form.totp_code.length !== 6}
                                    className={classNames(`flex w-full justify-center rounded-md ${bgColor} px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm ${hoverBgColor} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${focusVisible} ${disabledBg}`)}
                                >
                                    {loading ? (
                                        <LoadingCircleTransparent />
                                    ) : (
                                        translate('mfa.complete_setup') || 'Complete Setup'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
